import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, MessageCircle, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { fmtTRY, fmtQty4  } from "../lib/format";
import { errText } from "../lib/errors";
import api from "../lib/api";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

export default function CartPage() {
  const { items, setQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);

  useEffect(() => { api.get("/settings/public").then(({ data }) => setSettings(data)).catch(() => {}); }, []);
  useEffect(() => { if (items.length) trackEvent("view_cart", { currency: "TRY", value: Math.round(subtotal * 100) / 100, items: items.map((i) => ({ item_id: i.product_id, item_name: i.name, price: i.price, quantity: i.quantity })) }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const freeLimit = settings?.free_shipping_limit ?? 750;
  const shippingFee = settings?.shipping_fee ?? 49.9;
  const discount = applied?.discount ?? 0;
  const shipping = subtotal - discount >= freeLimit ? 0 : shippingFee;
  const total = subtotal - discount + shipping;
  const progress = Math.min(100, (subtotal / freeLimit) * 100);

  const applyCoupon = async () => {
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal });
      setApplied(data);
      toast.success(`Kupon uygulandı: ${fmtTRY(data.discount)} indirim`);
    } catch (err) { toast.error(errText(err) || "Kupon geçersiz"); }
  };

  const waOrder = () => {
    const lines = items.map((i) => `• ${i.name}${i.variant ? ` (${i.variant})` : ""} x${fmtQty4(i.quantity, i.unit || "")} — ${fmtTRY(i.price * i.quantity)}`).join("\n");
    const msg = `Merhaba, sipariş vermek istiyorum:\n${lines}\nToplam: ${fmtTRY(total)}`;
    trackEvent("whatsapp_click", { location: "sepet", value: Math.round(total * 100) / 100 });
    window.open(`https://wa.me/${settings?.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="max-w-2xl mx-auto px-4 py-24 text-center">
        <p className="font-serif text-3xl text-stone-800">Sepetiniz boş</p>
        <p className="text-stone-500 mt-3">Doğal lezzetlerimizi keşfedin.</p>
        <Link to="/urunler" className="inline-flex items-center gap-2 mt-8 bg-forest hover:bg-forest-dark text-white font-semibold px-7 py-3.5 rounded-xl transition-colors">Alışverişe Başla <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-7xl mx-auto px-4 py-10">
      <h1 className="font-serif text-3xl sm:text-4xl text-forest">Sepetim</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((i) => (
            <div key={`${i.product_id}-${i.variant}`} data-testid={`cart-item-${i.product_id}`} className="bg-white rounded-2xl border border-stone-200/80 p-4 flex gap-4 items-center">
              <img src={i.image} alt={i.name} className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover bg-cream-dark" />
              <div className="flex-1 min-w-0">
                <Link to={`/urun/${i.slug}`} className="font-semibold text-stone-900 text-sm sm:text-base line-clamp-1 hover:text-forest">{i.name}</Link>
                {i.variant && <p className="text-xs text-stone-500 mt-0.5">{i.variant}</p>}
                <p className="text-sm text-stone-500 mt-1">{fmtTRY(i.price)} / adet</p>
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex items-center border border-stone-300 rounded-lg">
                    <button data-testid={`qty-dec-${i.product_id}`} onClick={() => setQty(i.product_id, i.variant, i.quantity - 1)} className="p-1.5 hover:bg-cream-dark rounded-l-lg" aria-label="Azalt"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-8 text-center text-sm font-semibold">{fmtQty4(i.quantity, i.unit || "")}</span>
                    <button data-testid={`qty-inc-${i.product_id}`} onClick={() => setQty(i.product_id, i.variant, i.quantity + 1)} className="p-1.5 hover:bg-cream-dark rounded-r-lg" aria-label="Artır"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button data-testid={`remove-${i.product_id}`} onClick={() => removeItem(i.product_id, i.variant)} className="text-stone-400 hover:text-terra transition-colors" aria-label="Kaldır"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="font-bold text-forest whitespace-nowrap">{fmtTRY(i.price * i.quantity)}</p>
            </div>
          ))}
        </div>

        <aside className="bg-white rounded-2xl border border-stone-200/80 p-6 h-fit sticky top-40">
          <h2 className="font-semibold text-lg text-stone-900">Sipariş Özeti</h2>
          <div className="mt-4">
            <div className="flex justify-between text-xs text-stone-500 mb-1.5">
              <span>{subtotal >= freeLimit ? "Ücretsiz kargo kazandınız!" : `Ücretsiz kargo için ${fmtTRY(freeLimit - subtotal)} kaldı`}</span>
            </div>
            <div className="h-2 bg-cream-dark rounded-full overflow-hidden">
              <div data-testid="shipping-progress" className="h-full bg-gradient-to-r from-gold to-forest rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="flex gap-2 mt-5">
            <input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Kupon kodu" className="flex-1 h-11 px-3 rounded-xl border border-stone-300 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-forest/30" />
            <button data-testid="coupon-apply" onClick={applyCoupon} className="bg-stone-800 hover:bg-stone-900 text-white text-sm font-semibold px-4 rounded-xl transition-colors">Uygula</button>
          </div>
          <dl className="mt-5 space-y-2.5 text-sm">
            <div className="flex justify-between"><dt className="text-stone-500">Ara Toplam</dt><dd className="font-medium">{fmtTRY(subtotal)}</dd></div>
            {discount > 0 && <div className="flex justify-between text-green-700"><dt>İndirim ({applied.code})</dt><dd>-{fmtTRY(discount)}</dd></div>}
            <div className="flex justify-between"><dt className="text-stone-500">Kargo</dt><dd className="font-medium">{shipping === 0 ? "Ücretsiz" : fmtTRY(shipping)}</dd></div>
            <div className="flex justify-between border-t border-stone-200 pt-3 text-base"><dt className="font-semibold">Genel Toplam</dt><dd data-testid="cart-total" className="font-bold text-forest">{fmtTRY(total)}</dd></div>
          </dl>
          <button data-testid="checkout-proceed-button" onClick={() => navigate("/odeme", { state: { coupon: applied?.code } })} className="w-full mt-6 bg-forest hover:bg-forest-dark text-white font-semibold py-3.5 rounded-xl transition-colors">Siparişi Tamamla</button>
          <button data-testid="whatsapp-order-button" onClick={waOrder} className="w-full mt-3 bg-[#25D366] hover:bg-[#20ba5a] text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm">
            <MessageCircle className="w-4 h-4" /> WhatsApp'tan Sipariş Ver
          </button>
        </aside>
      </div>
    </div>
  );
}
